import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";

const empty = { title: "", excerpt: "", body: "", category: "Stories", cover_image: "", published: true };

export default function AdminDashboard() {
  const { user, ready, logout } = useAuth();
  const [posts, setPosts] = useState([]);
  const [editing, setEditing] = useState(null); // null | 'new' | postObject
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/posts").then((r) => setPosts(r.data)).catch(() => {});
  useEffect(() => { if (user) load(); }, [user]);

  if (!ready) return <main className="pt-40 px-6"><div className="h-eyebrow">Loading…</div></main>;
  if (!user) return <Navigate to="/admin/login" replace />;

  const openNew = () => { setForm(empty); setEditing("new"); };
  const openEdit = (p) => { setForm({ title: p.title, excerpt: p.excerpt, body: p.body, category: p.category, cover_image: p.cover_image, published: p.published }); setEditing(p); };
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing === "new") {
        await api.post("/admin/posts", form);
        toast.success("Entry published.");
      } else {
        await api.put(`/admin/posts/${editing.id}`, form);
        toast.success("Entry updated.");
      }
      setEditing(null);
      load();
    } catch { toast.error("Save failed."); }
    finally { setSaving(false); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/posts/${p.id}`);
      toast.success("Entry removed.");
      load();
    } catch { toast.error("Delete failed."); }
  };

  return (
    <main className="pt-24 md:pt-28 min-h-screen" data-testid="page-admin">
      <section className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 border-b border-white/10 pb-10">
          <div>
            <div className="h-eyebrow mb-4">§ Studio Portal</div>
            <h1 className="h-display text-4xl md:text-6xl leading-none">The Journal — <span className="italic font-serif-editorial text-[var(--gold)]">Editor</span></h1>
            <p className="font-serif-editorial italic text-[var(--ink-muted)] mt-3">Signed in as {user.email}</p>
          </div>
          <div className="flex items-center gap-6">
            <button onClick={openNew} data-testid="admin-new-post" className="bg-[var(--royal)] hover:bg-[var(--royal-soft)] text-white px-6 py-3 font-sans-display uppercase tracking-[0.3em] text-[11px] transition-colors">+ New Entry</button>
            <button onClick={logout} data-testid="admin-logout" className="font-sans-display uppercase tracking-[0.3em] text-[11px] text-[var(--ink-muted)] hover:text-[var(--gold)]">Sign out</button>
          </div>
        </div>

        {editing && (
          <form onSubmit={save} className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-8 border border-white/10 p-8 md:p-12" data-testid="admin-post-editor">
            <label className="md:col-span-2 block">
              <span className="h-eyebrow block mb-2">Title</span>
              <input required className="line-input" value={form.title} onChange={set("title")} data-testid="editor-title" />
            </label>
            <label className="block">
              <span className="h-eyebrow block mb-2">Category</span>
              <input className="line-input" value={form.category} onChange={set("category")} data-testid="editor-category" />
            </label>
            <label className="block">
              <span className="h-eyebrow block mb-2">Cover Image URL</span>
              <input className="line-input" value={form.cover_image} onChange={set("cover_image")} data-testid="editor-cover" />
            </label>
            <label className="md:col-span-2 block">
              <span className="h-eyebrow block mb-2">Excerpt</span>
              <input className="line-input" value={form.excerpt} onChange={set("excerpt")} data-testid="editor-excerpt" />
            </label>
            <label className="md:col-span-2 block">
              <span className="h-eyebrow block mb-2">Body (Markdown supported)</span>
              <textarea rows={12} required className="line-input font-mono-fine text-sm" value={form.body} onChange={set("body")} data-testid="editor-body" />
            </label>
            <label className="flex items-center gap-3 text-[var(--ink)]">
              <input type="checkbox" checked={form.published} onChange={set("published")} data-testid="editor-published" /> <span className="h-eyebrow">Published</span>
            </label>
            <div className="md:col-span-2 flex gap-6 pt-4 border-t border-white/5">
              <button type="submit" disabled={saving} className="bg-[var(--royal)] hover:bg-[var(--royal-soft)] text-white px-8 py-3 font-sans-display uppercase tracking-[0.3em] text-[11px] transition-colors disabled:opacity-60" data-testid="editor-save">
                {saving ? "Saving…" : editing === "new" ? "Publish" : "Update"}
              </button>
              <button type="button" onClick={() => setEditing(null)} className="font-sans-display uppercase tracking-[0.3em] text-[11px] text-[var(--ink-muted)] hover:text-[var(--ink)]" data-testid="editor-cancel">Cancel</button>
            </div>
          </form>
        )}

        <ul className="mt-10 divide-y divide-white/5">
          {posts.map((p) => (
            <li key={p.id} className="py-6 flex items-center justify-between gap-6" data-testid={`admin-post-${p.slug}`}>
              <div className="min-w-0">
                <div className="flex items-center gap-3 mb-1">
                  <span className={`h-eyebrow ${p.published ? "text-[var(--gold)]" : "text-[var(--ink-muted)]"}`}>{p.published ? "Live" : "Draft"}</span>
                  <span className="h-eyebrow text-[var(--ink-muted)]">{p.category}</span>
                </div>
                <div className="font-sans-display text-xl md:text-2xl truncate">{p.title}</div>
                <div className="font-serif-editorial italic text-[var(--ink-muted)] text-sm md:text-base truncate">{p.excerpt}</div>
              </div>
              <div className="flex items-center gap-5 shrink-0">
                <Link to={`/journal/${p.slug}`} target="_blank" className="font-sans-display uppercase tracking-[0.3em] text-[10px] text-[var(--ink-muted)] hover:text-[var(--gold)]">View</Link>
                <button onClick={() => openEdit(p)} className="font-sans-display uppercase tracking-[0.3em] text-[10px] text-[var(--gold)] hover:underline">Edit</button>
                <button onClick={() => remove(p)} className="font-sans-display uppercase tracking-[0.3em] text-[10px] text-red-400 hover:underline">Delete</button>
              </div>
            </li>
          ))}
        </ul>
      </section>
      <div className="h-32" />
    </main>
  );
}
